import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { ISubscriber } from '../../api-client/autogenerated';
import { getSubscribers } from '../../models/api/subscribers';
import {
  createPartnerApiKey,
  editPartnerApiKey,
  getPartnerApiKeyStats,
  IEditPartnerApiKeyRequest,
  IPartnerApiKeyStatsResponse,
  IPartnerApiKeyView,
  listPartnerApiKeys,
  revealPartnerApiKey,
} from '../../models/api/partner-api-key';
import { CancelButton } from '../custom-components/CustomButtons';
import { formatDate } from '../../scripts/utils';

/**
 * Site-admin management of partner API keys: list, create, edit, and reveal the raw key (pulled from
 * Secrets Manager on demand — every reveal is audited server-side). The "Partner API Keys" section
 * shows a button that opens a dialog containing the toolbar + table, matching the rest of the
 * site-admin dashboard. Access is already gated to site admins by the dashboard.
 */
const EMPTY_CREATE = {
  subscriberId: '',
  name: '',
  description: '',
  isSandbox: false,
  rateLimitPerMinute: '60',
  expiresAt: '',
};

const PartnerApiKeysAdmin: React.FC = () => {
  const [managerOpen, setManagerOpen] = useState(false);
  const [keys, setKeys] = useState<IPartnerApiKeyView[]>([]);
  const [subscribers, setSubscribers] = useState<ISubscriber[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_CREATE });

  const [editKey, setEditKey] = useState<IPartnerApiKeyView | null>(null);
  const [editForm, setEditForm] = useState<IEditPartnerApiKeyRequest>({});

  const [revealed, setRevealed] = useState<{ name: string; apiKey: string } | null>(null);

  const [statsKeyName, setStatsKeyName] = useState<string | null>(null);
  const [stats, setStats] = useState<IPartnerApiKeyStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setKeys(await listPartnerApiKeys());
    } catch (e) {
      setError('Failed to load partner API keys.');
    }
  }, []);

  // Load lazily when the manager dialog is opened.
  useEffect(() => {
    if (!managerOpen) return;
    refresh();
    getSubscribers(1000)
      .then(setSubscribers)
      .catch(() => setError('Failed to load subscribers.'));
  }, [managerOpen, refresh]);

  const handleCreate = async () => {
    try {
      await createPartnerApiKey({
        subscriberId: createForm.subscriberId.trim(),
        name: createForm.name.trim(),
        description: createForm.description.trim() || null,
        isSandbox: createForm.isSandbox,
        rateLimitPerMinute: Number(createForm.rateLimitPerMinute) || 60,
        expiresAt: createForm.expiresAt.trim() || null,
      });
      setCreateOpen(false);
      setCreateForm({ ...EMPTY_CREATE });
      await refresh();
    } catch (e) {
      setError('Failed to create key. Check the subscriber id and inputs.');
    }
  };

  const openEdit = (key: IPartnerApiKeyView) => {
    setEditKey(key);
    setEditForm({
      name: key.name,
      description: key.description ?? '',
      disabled: key.disabled,
      rateLimitPerMinute: key.rateLimitPerMinute,
      expiresAt: key.expiresAt ?? '',
    });
  };

  const handleEdit = async () => {
    if (!editKey) return;
    try {
      await editPartnerApiKey(editKey.id, {
        ...editForm,
        description: (editForm.description ?? '') === '' ? null : editForm.description,
        expiresAt: (editForm.expiresAt ?? '') === '' ? null : editForm.expiresAt,
      });
      setEditKey(null);
      await refresh();
    } catch (e) {
      setError('Failed to update key.');
    }
  };

  const handleReveal = async (key: IPartnerApiKeyView) => {
    try {
      const res = await revealPartnerApiKey(key.id);
      setRevealed({ name: key.name, apiKey: res.apiKey });
    } catch (e) {
      setError('Failed to reveal key.');
    }
  };

  const handleViewStats = async (key: IPartnerApiKeyView) => {
    setStatsKeyName(key.name);
    setStats(null);
    setStatsError(null);
    setStatsLoading(true);
    try {
      setStats(await getPartnerApiKeyStats(key.id));
    } catch (e) {
      setStatsError('Failed to load stats for this key.');
    } finally {
      setStatsLoading(false);
    }
  };

  return (
    <>
      <Button variant="contained" onClick={() => setManagerOpen(true)}>
        Manage Partner API Keys
      </Button>

      {/* Manager dialog: toolbar + table */}
      <Dialog open={managerOpen} onClose={() => setManagerOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Partner API Keys</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} style={{ marginBottom: '12px' }}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setCreateForm({ ...EMPTY_CREATE });
                  setCreateOpen(true);
                }}
              >
                Create new key
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" style={{ marginBottom: '8px' }}>
              {error}
            </Typography>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow style={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Subscriber</TableCell>
                  <TableCell>Env</TableCell>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Rate/min</TableCell>
                  <TableCell>Disabled</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>{key.subscriberName ?? key.subscriberId}</TableCell>
                    <TableCell>{key.isSandbox ? 'sandbox' : 'live'}</TableCell>
                    <TableCell>{key.keyPrefix}</TableCell>
                    <TableCell>{key.rateLimitPerMinute}</TableCell>
                    <TableCell>{key.disabled ? 'yes' : 'no'}</TableCell>
                    <TableCell>{key.expiresAt ?? '—'}</TableCell>
                    <TableCell>{key.lastUsedAt ? formatDate(key.lastUsedAt) : 'never'}</TableCell>
                    <TableCell align="center">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => openEdit(key)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleReveal(key)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Reveal Key
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleViewStats(key)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Stats
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
        <DialogTitle>Create partner API key</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <Select
              variant="outlined"
              displayEmpty
              value={createForm.subscriberId}
              onChange={(e) =>
                setCreateForm({ ...createForm, subscriberId: e.target.value as string })
              }
              renderValue={(selected) => {
                const id = selected as string;
                if (!id)
                  return (
                    <span
                      style={{ color: 'rgba(0, 0, 0, 0.4)', fontStyle: 'italic', marginLeft: 4 }}
                    >
                      Subscriber
                    </span>
                  );
                return subscribers.find((s) => s.id === id)?.name ?? id;
              }}
            >
              <MenuItem value="" disabled>
                Subscriber
              </MenuItem>
              {subscribers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            label="Name"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
          />
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            label="Description"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
          />
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            type="number"
            label="Rate limit per minute"
            value={createForm.rateLimitPerMinute}
            onChange={(e) => setCreateForm({ ...createForm, rateLimitPerMinute: e.target.value })}
          />
          <KeyboardDateTimePicker
            clearable
            fullWidth
            margin="dense"
            inputVariant="outlined"
            format="MM/DD/YYYY hh:mm A"
            label="Expires at (optional)"
            value={createForm.expiresAt || null}
            onChange={(date) =>
              setCreateForm({
                ...createForm,
                expiresAt: date && date.isValid() ? date.toISOString() : '',
              })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={createForm.isSandbox}
                onChange={(e) => setCreateForm({ ...createForm, isSandbox: e.target.checked })}
              />
            }
            label="Sandbox key"
          />
        </DialogContent>
        <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
          <CancelButton onClick={() => setCreateOpen(false)}>Cancel</CancelButton>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={!createForm.subscriberId || !createForm.name}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editKey} onClose={() => setEditKey(null)} fullWidth>
        <DialogTitle>Edit partner API key</DialogTitle>
        <DialogContent>
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            label="Name"
            value={editForm.name ?? ''}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            label="Description"
            value={editForm.description ?? ''}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
          />
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            type="number"
            label="Rate limit per minute"
            value={editForm.rateLimitPerMinute ?? 60}
            onChange={(e) =>
              setEditForm({ ...editForm, rateLimitPerMinute: Number(e.target.value) || 60 })
            }
          />
          <KeyboardDateTimePicker
            clearable
            fullWidth
            margin="dense"
            inputVariant="outlined"
            format="MM/DD/YYYY hh:mm A"
            label="Expires at (clear to remove)"
            value={editForm.expiresAt || null}
            onChange={(date) =>
              setEditForm({
                ...editForm,
                expiresAt: date && date.isValid() ? date.toISOString() : '',
              })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={editForm.disabled ?? false}
                onChange={(e) => setEditForm({ ...editForm, disabled: e.target.checked })}
              />
            }
            label="Disabled (revoked)"
          />
        </DialogContent>
        <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
          <CancelButton onClick={() => setEditKey(null)}>Cancel</CancelButton>
          <Button variant="contained" color="primary" onClick={handleEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reveal dialog */}
      <Dialog open={!!revealed} onClose={() => setRevealed(null)} fullWidth>
        <DialogTitle>Raw key — {revealed?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Copy this now and deliver it securely. This reveal has been audited. The key is not
            stored anywhere it can be read again except Secrets Manager.
          </DialogContentText>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            value={revealed?.apiKey ?? ''}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
          <CancelButton onClick={() => setRevealed(null)}>Close</CancelButton>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (revealed?.apiKey) navigator.clipboard?.writeText(revealed.apiKey);
            }}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats dialog */}
      <Dialog open={!!statsKeyName} onClose={() => setStatsKeyName(null)} maxWidth="md" fullWidth>
        <DialogTitle>Usage stats — {statsKeyName}</DialogTitle>
        <DialogContent>
          {statsLoading && (
            <Grid container justify="center" style={{ padding: 24 }}>
              <CircularProgress />
            </Grid>
          )}
          {statsError && <Typography color="error">{statsError}</Typography>}
          {stats && !statsLoading && (
            <>
              <Typography variant="h6" style={{ marginBottom: 12 }}>
                Total calls: {stats.totalCalls}
              </Typography>

              <Typography variant="subtitle1" style={{ marginBottom: 4 }}>
                Calls by endpoint
                {stats.uniqueEndpointCount > stats.callsByEndpoint.length &&
                  ` (top ${stats.callsByEndpoint.length} of ${stats.uniqueEndpointCount})`}
              </Typography>
              <TableContainer component={Paper} style={{ marginBottom: 20 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell>Method</TableCell>
                      <TableCell>Resource</TableCell>
                      <TableCell align="right">Calls</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.callsByEndpoint.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>No calls recorded yet.</TableCell>
                      </TableRow>
                    )}
                    {stats.callsByEndpoint.map((row) => (
                      <TableRow key={`${row.method} ${row.path}`}>
                        <TableCell>{row.method}</TableCell>
                        <TableCell>{row.path}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" style={{ marginBottom: 4 }}>
                Calls by IP address
                {stats.uniqueIpCount > stats.callsByIp.length &&
                  ` (top ${stats.callsByIp.length} of ${stats.uniqueIpCount})`}
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell>IP address</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Calls</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.callsByIp.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>No calls recorded yet.</TableCell>
                      </TableRow>
                    )}
                    {stats.callsByIp.map((row) => (
                      <TableRow key={row.ip}>
                        <TableCell>{row.ip}</TableCell>
                        <TableCell>{row.location ?? '—'}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsKeyName(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerApiKeysAdmin;
